import { v4 as uuidV4 } from "uuid";

import type {
  CustomerMinimal,
  FileEntry,
  Group,
} from "../state/types";
import {
  buildGroupsFromMatches,
  type ScoredFileInput,
} from "./buildGroupsFromMatches";
import { scoreFileAgainstCustomers } from "./scoreCandidates";

export type RunMatcherResult = {
  files: Record<string, FileEntry>;
  groups: Group[];
  ungrouped_file_ids: string[];
};

/**
 * Public matching entry point. Pure: no React, no fetch, no mutation of
 * inputs.
 *
 * - Assigns a fresh local uuid to every input `File` so the wizard can
 *   key state by stable ids (filenames are not unique).
 * - Captures `original_index` from the input order so duplicate filenames
 *   still sort deterministically downstream (e.g. backend upload order).
 * - Computes the top suggestions per file once. These are immutable for
 *   the lifetime of the wizard (group-level suggestion chips are derived
 *   elsewhere from current group membership).
 * - Delegates bucket assignment and suggested-group construction to
 *   `buildGroupsFromMatches`.
 */
export function runMatcher(args: {
  files: File[];
  customers: CustomerMinimal[];
}): RunMatcherResult {
  const scoredInputs: ScoredFileInput[] = [];
  const filesById: Record<string, FileEntry> = {};

  args.files.forEach((file: File, originalIndex: number) => {
    const id = uuidV4();
    const suggestions = scoreFileAgainstCustomers(file.name, args.customers);
    scoredInputs.push({ id, suggestions });
    filesById[id] = {
      id,
      file,
      original_index: originalIndex,
      suggestions,
      // Placeholder bucket; replaced below using the build-groups output.
      initial_bucket: 3,
    };
  });

  const built = buildGroupsFromMatches({
    scored_files: scoredInputs,
    customers: args.customers,
  });

  for (const assignment of built.file_buckets) {
    const entry = filesById[assignment.id];
    if (entry) {
      entry.initial_bucket = assignment.initial_bucket;
    }
  }

  return {
    files: filesById,
    groups: built.groups,
    ungrouped_file_ids: built.ungrouped_file_ids,
  };
}

export { scoreFileAgainstCustomer, scoreFileAgainstCustomers } from "./scoreCandidates";
export { buildGroupsFromMatches } from "./buildGroupsFromMatches";
export { normalizeForMatch, stripLegalSuffixes } from "./tokenize";
